import { ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';

import type { User } from '@revelio/data-access';

import type { StatusDetails } from './impersonationModal.utils';
import { UserImpersonationSearch } from './UserImpersonationSearch';

interface ImpersonationSearchCardProps {
  selectedUser: Partial<User> | null | undefined;
  activeStatus: StatusDetails;
  trialStatus: StatusDetails;
  onSelectUser: (user: Partial<User>) => void;
  onClearSelection: () => void;
  onEditOverrides: () => void;
}

export function ImpersonationSearchCard({
  selectedUser,
  activeStatus,
  trialStatus,
  onSelectUser,
  onClearSelection,
  onEditOverrides,
}: ImpersonationSearchCardProps) {
  return (
    <VStack
      spacing={3}
      align="stretch"
      data-testid="search-user-to-impersonate"
    >
      <Box>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Search for a user to impersonate across client groups.
        </Text>
        <UserImpersonationSearch onSelectUser={onSelectUser} />
      </Box>

      {selectedUser && (
        <Box
          p={3}
          border="1px solid"
          borderColor="blue.400"
          borderRadius="md"
          bg="blue.50"
          boxShadow="0 0 0 1px var(--chakra-colors-blue-400)"
        >
          <Flex justify="space-between" align="center" gap={3}>
            <Box minW={0} flex="1">
              <Flex align="center" gap={2} mb={1}>
                <Badge colorScheme="blue" fontSize="2xs">
                  Selected
                </Badge>
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {selectedUser.name || selectedUser.email || 'Unknown user'}
                </Text>
              </Flex>
              <Text fontSize="xs" color="gray.600" noOfLines={1}>
                {selectedUser.email || selectedUser.username || 'No email'}
                {selectedUser.client_name
                  ? ` · Client Group: ${selectedUser.client_name}`
                  : ''}
              </Text>
            </Box>
            <Flex gap={1} flexShrink={0} align="center">
              <Badge colorScheme={trialStatus.colorScheme} fontSize="2xs">
                {trialStatus.label}
              </Badge>
              <Badge colorScheme={activeStatus.colorScheme} fontSize="2xs">
                {activeStatus.label}
              </Badge>
              <IconButton
                aria-label="Clear selected user"
                icon={<CloseIcon boxSize={2} />}
                size="xs"
                variant="ghost"
                onClick={onClearSelection}
                data-testid="clear-selected-user-button"
              />
            </Flex>
          </Flex>

          <HStack justify="flex-end" pt={2}>
            <Button
              variant="ghost"
              size="sm"
              colorScheme="blue"
              rightIcon={<ChevronRightIcon />}
              onClick={onEditOverrides}
              data-testid="edit-search-overrides-button"
            >
              Edit overrides
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
}
